import { useEffect, useState } from "react";
import { MdAdd } from "react-icons/md";
import {
  GenericTable,
  RoleValidator,
  Pagination,
  Spinner,
  FilterCard,
} from "../../Components/Common";
import { useNavigate } from "react-router-dom";
import { useSites } from "./Hooks/useSites";
import { useProjects } from "../Project/Hooks/useProjects";
import { CREATE_SITE, SITE_DETAILS } from "../../routes";
import {
  MESSAGES,
  SITE_COLUMNS_LABELS,
  SITE_LIST_COLUMNS,
  SITE_STATUS,
  SITE_STATUS_STYLES,
  SITE_STATUS_LABELS,
} from "./Constants";
import { toProperCase } from "../../Utils";
import { ROLES } from "../../Constants/Strings";

const SiteList = () => {
  const {
    sites,
    loading,
    filters,
    error,
    setFilters,
    page,
    onPageChange,
    onPageSizeChange,
    totalPages,
    rowsPerPage,
    fetchSites,
  } = useSites();

  const { projects, fetchProjects } = useProjects();
  const navigate = useNavigate();
  const [searchText, setSearchText] = useState("");

  useEffect(() => {
    fetchSites({
      sites: filters.sites || "all", // maps frontend 'site' to backend 'sites'
      project: filters.project || "", // project filter
      status: filters.status || "", // status filter
      search: filters.search || "", // search filter
      limit: rowsPerPage,
      offset: (page - 1) * rowsPerPage,
    });
  }, [filters, page, rowsPerPage]);

  useEffect(() => {
    fetchProjects();
  }, []);

  const actionsRenderField = (key, row) => {
    switch (key) {
      case "completion_percentage":
        return (
          <div className="w-1/2 flex justify-center">
            {row[key] ? `${row[key]}%` : "0%"}
          </div>
        );

      case "status":
        return (
          <span
            className={`px-2 py-0.5 rounded-full text-xs font-medium shrink-0 ${
              SITE_STATUS_STYLES[row.status] || SITE_STATUS_STYLES.default
            }`}
          >
            {SITE_STATUS_LABELS[row.status] || "-"}
          </span>
        );

      default:
        return row[key] || "-";
    }
  };

  const renderMobileCard = (row) => (
    <div
      className="px-4"
      key={row.id}
      onClick={() => navigate(SITE_DETAILS.replace(":instanceId", row.id))}
    >
      <div className="w-full grid grid-cols-2 bg-white hover:shadow-md transition border-b border-gray-400 py-3">
        <div className="flex flex-col justify-between items-start w-[300px]">
          <h3
            className="text-base font-semibold text-gray-900 truncate max-w-[50%]"
            title={row.site_name}
          >
            {row.site_name || "Untitled Site"}
          </h3>
          <div className="flex gap-2 flex-wrap truncate text-sm max-w-[70%]">
            {toProperCase(row.region) || "-"} | {toProperCase(row.zone) || "-"}{" "}
            | {toProperCase(row.state) || "-"} |{" "}
            {toProperCase(row.district) || "-"}
          </div>
        </div>
        <div className="flex items-center gap-10 justify-end">
          <span
            className={`px-2 py-0.5 rounded-full text-xs font-medium shrink-0 ${
              SITE_STATUS_STYLES[row.status] || SITE_STATUS_STYLES.default
            }`}
          >
            {SITE_STATUS_LABELS[row.status] || "-"}
          </span>
          <div className="flex gap-2 flex-wrap">
            <p>
              {row.completion_percentage
                ? `${row.completion_percentage}%`
                : "0%"}
            </p>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="overflow-y-hidden">
      <div className="grid grid-cols-12 items-center gap-4 px-3 py-4 mt-3">
        <div className="col-span-12 md:col-span-11 flex items-center justify-between pl-2">
          <h1 className="text-2xl font-bold">Sites</h1>
          <RoleValidator
            allowedRoles={[
              ROLES.MANAGING_DIRECTOR,
              ROLES.HEAD_OFFICE,
              ROLES.GOVERNMENT_OFFICER,
              ROLES.PROJECT_MANAGER,
              ROLES.PROJECT_ENGINEER,
            ]}
            mode="disable"
          >
            {/* Mobile Add Site */}
            <button
              onClick={() => navigate(CREATE_SITE)}
              className="md:hidden bg-primary text-white px-3 py-2 rounded-md flex items-center gap-2"
              aria-label="Add Site"
            >
              <MdAdd size={18} />
              <span className="whitespace-nowrap">Add Site</span>
            </button>
          </RoleValidator>
        </div>

        {/* Desktop Add */}
        <div className="hidden md:flex md:col-span-1 justify-end">
          <button
            onClick={() => navigate(CREATE_SITE)}
            className="bg-primary text-white px-3 py-2 rounded-md flex items-center gap-2"
          >
            <MdAdd size={18} />
            <span className="whitespace-nowrap">Add Site</span>
          </button>
        </div>
      </div>

      {error && <p className="text-red-500 mb-4">{error}</p>}

      {/* Search + Filters Card */}
      <div className="mb-4 px-3">
        <div>
          <FilterCard
            tempFilters={filters}
            setTempFilters={(updatedFilters) => {
              setFilters(updatedFilters);
              onPageChange(1);
            }}
            searchText={searchText}
            setSearchText={setSearchText}
            fields={[
              {
                key: "search",
                label: "Search",
                type: "search",
                placeholder: "Search by site name",
              },
              {
                key: "sites",
                label: "All Sites",
                type: "select",
                options: [
                  { value: "my", label: "My Sites" },
                  { value: "all", label: "All Sites" },
                ],
              },
              {
                key: "status",
                label: "Status",
                type: "select",
                options: [{ value: "", label: "All" }, ...SITE_STATUS],
              },
              {
                key: "project",
                label: "Project",
                type: "select",
                options: [
                  { value: "", label: "All Projects" },
                  ...projects.map((p) => ({ value: p.id, label: p.name })),
                ],
              },
            ]}
          />
        </div>
      </div>

      {/* Sites Table */}
      <div className="flex flex-col md:flex-row gap-6 h-[68vh] md:h-[57vh] px-3">
        {loading ? (
          <div className="flex justify-center items-center w-full h-full">
            <Spinner />
          </div>
        ) : (
          <div className="w-full flex flex-col h-full">
            <div className="w-full flex flex-col gap-4 flex-1 overflow-y-auto">
              <GenericTable
                tableColumns={SITE_LIST_COLUMNS}
                columnLabels={SITE_COLUMNS_LABELS}
                tableRows={sites}
                error={MESSAGES.NO_SITE_FOUND}
                mobileGridColumns={4}
                enableGridLayout={true}
                renderMobileCard={renderMobileCard}
                renderField={actionsRenderField}
                rowClick={(row) =>
                  navigate(SITE_DETAILS.replace(":instanceId", row.id))
                }
              />
            </div>
            <div className="shrink-0 mt-2">
              <Pagination
                page={page}
                rowsPerPage={rowsPerPage}
                onPageChange={onPageChange}
                onPageSizeChange={onPageSizeChange}
                totalPages={totalPages}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SiteList;
